import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useAuth, AuthProvider } from "./context/AuthContext.tsx";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { lightTheme, darkTheme } from './utils/theme.ts';
import Layout from './components/Layout.tsx';
import LoginPage from "./pages/LoginPage.tsx";
import HomePage from "./pages/HomePage.tsx";
import TourPage from './pages/TourPage.tsx';
import ParentPage from './pages/ParentPage.tsx';
import ReportPage from './pages/ReportPage.tsx';
import DokkMonitorPage from './pages/DokkMonitorPage.tsx';

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const App: React.FC = () => {
  const prefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const savedMode = localStorage.getItem("darkMode");
    return savedMode !== null ? savedMode === "true" : prefersDarkMode;
  });

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode;
      // Save the new mode in localStorage
      localStorage.setItem("darkMode", newMode.toString());
      return newMode;
    });
  };

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode.toString());
  }, [darkMode]);

  return (
    <AuthProvider>
      <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout onToggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                    <HomePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/:pk"
              element={
                <ProtectedRoute>
                  <Layout onToggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                    <TourPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent"
              element={
                <ProtectedRoute>
                  <Layout onToggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                    <ParentPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ProtectedRoute>
                  <Layout onToggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                    <ReportPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dokk-monitor"
              element={
                <ProtectedRoute>
                  <Layout onToggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                    <DokkMonitorPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<LoginPage />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
